// ============================================================================
// Recurring parent eligibility.
// ----------------------------------------------------------------------------
// A DAILY or WEEKLY checklist can only be created inside a Task / Goal /
// Subtask whose start and end dates are both set. This helper centralises
// that check so every writer (create/type-swap/draft persist) and the type
// selector agree on the rule and the user-facing reason string.
// ============================================================================
import type { TimelineElement } from '../types/timelineElement'

export type RecurringParentEligibility =
  | { ok: true }
  | { ok: false; reason: string }

/** Shape most call sites can produce without importing TimelineElement in
 *  full (some callers only have a minimal projection). */
export type ParentEligibilityInput = {
  type?: TimelineElement['type'] | null
  startDate?: string | null
  endDate?: string | null
}

const isIsoDate = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)

export const canParentHostRecurring = (
  parent: ParentEligibilityInput | null | undefined,
): RecurringParentEligibility => {
  if (!parent) {
    return { ok: false, reason: 'Recurring checklists must be created inside a Task, Goal, or Subtask.' }
  }
  if (parent.type !== 'GOAL' && parent.type !== 'TASK' && parent.type !== 'SUBTASK') {
    return { ok: false, reason: 'Recurring checklists can only live inside a Task, Goal, or Subtask.' }
  }
  if (!isIsoDate(parent.startDate) || !isIsoDate(parent.endDate)) {
    return { ok: false, reason: 'Set a start date and end date on the parent before adding a recurring checklist.' }
  }
  if ((parent.endDate as string).slice(0, 10) < (parent.startDate as string).slice(0, 10)) {
    return { ok: false, reason: "The parent's end date is before its start date." }
  }
  return { ok: true }
}

/** Symbolic constant for the synthetic top-level recurring parent that is
 *  being retired. Detecting it lets writers reject legacy container ids. */
export const LEGACY_RECURRING_CONTAINER_PREFIX = 'recurring:'

export const isLegacyRecurringContainerId = (
  containerId: string | null | undefined,
): boolean =>
  typeof containerId === 'string' && containerId.startsWith(LEGACY_RECURRING_CONTAINER_PREFIX)

/** UI-side aggregate — decides whether an "Add recurring checklist"
 *  affordance (menu item, button, etc.) should render at the current
 *  spot. Composes `canParentHostRecurring` with the two shell-level
 *  disqualifiers (Board/Dashboard level, legacy synthetic parent) and
 *  short-circuits when the caller isn't under a recurring lens. */
export const evaluateRecurringAddEligibility = (input: {
  underRecurringLens: boolean
  activeContainerId: string | null | undefined
  workspaceDefaultContainerId: string
  parentElement: ParentEligibilityInput | null | undefined
}): RecurringParentEligibility => {
  if (!input.underRecurringLens) return { ok: true }
  if (!input.activeContainerId || input.activeContainerId === input.workspaceDefaultContainerId) {
    return { ok: false, reason: 'Open a Task, Goal, or Subtask to add a recurring checklist.' }
  }
  if (isLegacyRecurringContainerId(input.activeContainerId)) {
    return { ok: false, reason: 'Top-level recurring canvases are retired.' }
  }
  return canParentHostRecurring(input.parentElement)
}
